import { useEffect, useState } from "react";
import AdminShell from "@/components/AdminShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Search, RefreshCw, ExternalLink, Info, AlertTriangle } from "lucide-react";
import api, { errMsg } from "@/lib/api";
const VERDICT_STYLES = {
    PASS:    { icon: CheckCircle2, tint: "text-[#15803D]", label: "Indexed" },
    FAIL:    { icon: XCircle,      tint: "text-[#DC2626]", label: "Not indexed" },
    PARTIAL: { icon: AlertTriangle, tint: "text-[#D97706]", label: "Partial" },
    NEUTRAL: { icon: Info,          tint: "text-[#17212B]/50", label: "Unknown" },
};

export default function SuperAdminSearchRankings() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [apiDisabled, setApiDisabled] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const r = await api.get("/super-admin/search-rankings/");
            setData(r.data);
            // Detect the "API not enabled" 403 across any inspection row.
            const anyDisabled = (r.data.rows || []).some(
                (row) => (row.inspection?.error || "").toLowerCase().includes("api has not been used")
            );
            setApiDisabled(anyDisabled);
        } catch (e) {
            toast.error(errMsg(e, "Failed to load rankings"));
        } finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    async function refresh() {
        setRefreshing(true);
        try {
            const r = await api.post("/super-admin/search-rankings/refresh", { limit: 20 });
            const anyDisabled = (r.data.errors || []).some(
                (e) => (e.inspection_error || "").toLowerCase().includes("api has not been used") || (e.analytics_error || "").toLowerCase().includes("api has not been used")
            );
            if (anyDisabled) setApiDisabled(true);
            toast.success(`Refreshed ${r.data.refreshed} doctors · ${r.data.ok} OK`);
            await load();
        } catch (e) {
            toast.error(errMsg(e, "Refresh failed"));
        } finally { setRefreshing(false); }
    }

    async function inspectOne(url) {
        try {
            await api.post("/super-admin/search-rankings/inspect", { url });
            toast.success("Inspection refreshed");
            load();
        } catch (e) { toast.error("Inspection failed"); }
    }

    async function resubmit(url) {
        try {
            await api.post("/super-admin/indexing/test", { url });
            toast.success("Re-submitted to Google + IndexNow");
        } catch (e) { toast.error("Re-submit failed"); }
    }

    return (
        <AdminShell>
            <div className="mb-6" data-testid="rankings-header">
                <div className="text-xs uppercase tracking-widest text-[#123B5D]/60">Super admin</div>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <h1 className="text-2xl font-bold text-[#123B5D]">Google Search Rankings</h1>
                    <Button onClick={refresh} disabled={refreshing} size="sm" data-testid="rankings-refresh-all">
                        <RefreshCw size={14} className={"mr-1.5 " + (refreshing ? "animate-spin" : "")} />
                        {refreshing ? "Refreshing…" : "Refresh (batch 20)"}
                    </Button>
                </div>
                <p className="text-[#17212B]/60 text-sm mt-1">
                    Property: <span className="font-mono text-xs">{data?.property_url || "—"}</span>
                </p>
            </div>

            {apiDisabled && (
                <Card className="mb-4 border-[#D97706]/40 bg-[#D97706]/5" data-testid="api-disabled-banner">
                    <CardContent className="p-4 text-sm">
                        <div className="flex items-start gap-2">
                            <AlertTriangle size={16} className="text-[#D97706] mt-0.5 shrink-0" />
                            <div>
                                <div className="font-semibold text-[#D97706]">Google Search Console API is not enabled yet.</div>
                                <div className="text-[#17212B]/70 mt-1">
                                    One-time click: open the Cloud Console → click <strong>Enable</strong> → wait ~2 min → hit Refresh here.
                                </div>
                                <a href="https://console.developers.google.com/apis/api/searchconsole.googleapis.com/overview?project=786687801205" target="_blank" rel="noreferrer" className="text-[#2563EB] font-semibold inline-flex items-center gap-1 mt-2" data-testid="enable-api-link">
                                    Enable Search Console API <ExternalLink size={11} />
                                </a>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {data?.notice && (
                <div className="rounded-lg border border-[#D97706]/30 bg-[#D97706]/5 p-3 text-xs text-[#17212B]/80 mb-4 flex items-start gap-2" data-testid="rankings-notice">
                    <Info size={13} className="text-[#D97706] mt-0.5 shrink-0" />
                    <span>{data.notice}</span>
                </div>
            )}

            {/* Summary cards */}
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6" data-testid="rankings-summary">
                {[
                    ["Published", data?.summary?.total_published],
                    ["Indexed", data?.summary?.indexed],
                    ["Not indexed", data?.summary?.not_indexed],
                    ["Unknown", data?.summary?.unknown],
                    ["Impressions (28d)", data?.summary?.total_impressions],
                    ["Clicks (28d)", data?.summary?.total_clicks],
                ].map(([label, val]) => (
                    <Card key={label} className="border-[#123B5D]/10">
                        <CardContent className="p-3">
                            <div className="text-[10px] uppercase tracking-widest text-[#17212B]/50">{label}</div>
                            <div className="text-xl font-bold text-[#123B5D] mt-1">{val ?? 0}</div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Table */}
            {loading ? (
                <div className="text-sm text-[#17212B]/60">Loading…</div>
            ) : (data?.rows || []).length === 0 ? (
                <Card className="border-[#123B5D]/10"><CardContent className="p-8 text-center text-sm text-[#17212B]/60">No published doctors yet.</CardContent></Card>
            ) : (
                <div className="overflow-hidden border border-[#E5E9F2] rounded-lg" data-testid="rankings-table">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-[#F5F7FB]">
                                <tr className="text-left text-[10px] uppercase tracking-widest text-[#17212B]/50">
                                    <th className="p-3">Doctor</th>
                                    <th className="p-3">Index status</th>
                                    <th className="p-3">Last crawl</th>
                                    <th className="p-3">Avg pos</th>
                                    <th className="p-3">Impr.</th>
                                    <th className="p-3">Clicks</th>
                                    <th className="p-3">Last submit</th>
                                    <th className="p-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.rows.map((r) => {
                                    const verdict = r.inspection?.verdict || "NEUTRAL";
                                    const V = VERDICT_STYLES[verdict] || VERDICT_STYLES.NEUTRAL;
                                    const VIcon = V.icon;
                                    return (
                                        <tr key={r.id} className="border-t border-[#E5E9F2]" data-testid={`ranking-row-${r.slug}`}>
                                            <td className="p-3">
                                                <div className="font-semibold text-[#123B5D]">{r.full_name}</div>
                                                <a href={r.url} target="_blank" rel="noreferrer" className="text-xs font-mono text-[#2563EB] hover:underline">/dr/{r.slug}</a>
                                            </td>
                                            <td className="p-3">
                                                <span className={`inline-flex items-center gap-1 text-xs ${V.tint}`}>
                                                    <VIcon size={12} /> {V.label}
                                                </span>
                                                {r.inspection?.coverage_state && (
                                                    <div className="text-[10px] text-[#17212B]/50 mt-0.5">{r.inspection.coverage_state}</div>
                                                )}
                                            </td>
                                            <td className="p-3 text-xs text-[#17212B]/70">
                                                {r.inspection?.last_crawl_time ? new Date(r.inspection.last_crawl_time).toLocaleDateString() : "—"}
                                            </td>
                                            <td className="p-3 text-xs">{r.analytics?.position ?? "—"}</td>
                                            <td className="p-3 text-xs">{r.analytics?.impressions ?? 0}</td>
                                            <td className="p-3 text-xs">{r.analytics?.clicks ?? 0}</td>
                                            <td className="p-3 text-[10px] text-[#17212B]/60">
                                                {r.last_submission
                                                    ? (() => {
                                                        const code = r.last_submission.google?.status_code;
                                                        const ok = r.last_submission.google?.ok;
                                                        const tip = code === 429
                                                            ? "Google daily quota exhausted — resets 07:00 UTC"
                                                            : ok
                                                                ? "Google Indexing API accepted the submission"
                                                                : `Google returned HTTP ${code || "?"} — see log for details`;
                                                        return <>{new Date(r.last_submission.sent_at).toLocaleDateString()}<br /><span title={tip} className={ok ? "text-[#15803D] cursor-help" : "text-[#DC2626] cursor-help"} data-testid={`gsc-submit-code-${r.slug}`}>G {code || "—"}</span></>;
                                                    })()
                                                    : "—"}
                                            </td>
                                            <td className="p-3">
                                                <div className="flex gap-1">
                                                    <button onClick={() => inspectOne(r.url)} className="text-[10px] font-semibold text-[#2563EB] hover:underline" data-testid={`inspect-${r.slug}`}>Inspect</button>
                                                    <span className="text-[#94A3B8]">·</span>
                                                    <button onClick={() => resubmit(r.url)} className="text-[10px] font-semibold text-[#0F766E] hover:underline" data-testid={`resubmit-${r.slug}`}>Re-submit</button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </AdminShell>
    );
}
